import { CANVAS_WIDTH } from "../../game/constants";
import type { Rect, UiRects } from "../../game/input";
import { resolveWorldMapCloseButtonRect } from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface WorldMapDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  nowSec: () => number;
  cloudImage: HTMLImageElement | null;
  drawCircleCloseButton: (rect: Rect) => void;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawWorldMapScreen(deps: WorldMapDeps): UiRects {
  const { ctx, canvasHeight, nowSec, cloudImage, drawCircleCloseButton } = deps;
  const cw = CANVAS_WIDTH;
  const t = nowSec();

  if (cloudImage != null) {
    const cloud = cloudImage;
    // Dense small cloud coverage from top to bottom
    // Exposed zones: neon city (mid-right ~x:0.35-0.85, y:0.12-0.28), bottom-right (x:0.4+, y:0.62+)
    const startY = -0.04;
    const cols = 6;
    const rows = 18;
    const yStep = (1.04 - startY) / rows;
    const xStep = 1.0 / cols;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const offX = (r % 2 === 0) ? 0 : xStep * 0.5;
        const baseX = c * xStep + offX - 0.08;
        const baseY = startY + r * yStep;
        // Skip neon city exposed zone (mid-right area)
        if (baseX > 0.3 && baseX < 0.9 && baseY > 0.10 && baseY < 0.30) continue;
        // Skip bottom-right exposed zone (jungle/waterfall)
        if (baseX > 0.4 && baseY > 0.62) continue;
        // Per-cloud variation from deterministic seed
        const seed = r * cols + c;
        const varX = Math.sin(seed * 3.7) * 0.04;
        const varY = Math.cos(seed * 2.3) * 0.02;
        const scale = 0.5 + (Math.sin(seed * 1.9) * 0.5 + 0.5) * 0.35;
        const driftSpeed = 0.1 + (Math.sin(seed * 4.1) * 0.5 + 0.5) * 0.2;
        const driftAmp = 2 + (Math.cos(seed * 2.7) * 0.5 + 0.5) * 4;

        const cloudW = cloud.naturalWidth * scale * 0.18;
        const cloudH = cloud.naturalHeight * scale * 0.18;
        const driftX = Math.sin(t * driftSpeed + seed * 1.3) * driftAmp;
        const px = (baseX + varX) * cw + driftX;
        const py = (baseY + varY) * canvasHeight - cloudH * 0.2;
        ctx.drawImage(cloud, px, py, cloudW, cloudH);
      }
    }
  }

  // Close button lowered and enlarged for the map screen.
  const backRect = resolveWorldMapCloseButtonRect(CANVAS_WIDTH);
  drawCircleCloseButton(backRect);

  return {
    worldMapBack: backRect,
  };
}
